import React, { useEffect, useMemo, useState } from 'react';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import EmptyState from '../../../components/shared/EmptyState';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import Pagination from '../../../components/shared/Pagination';
import en from '../../../translations/en.json';
import { paginateItems } from './documentsTabUtils';

function CasesNotesTab({ album, onSelectCase }) {
  const [casesList, setCasesList] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [casesPage, setCasesPage] = useState(1);
  const [notesPage, setNotesPage] = useState(1);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    let isMounted = true;

    const loadAlbumNotesAndCases = async () => {
      if (!album?.id) {
        if (isMounted) {
          setCasesList([]);
          setNotesList([]);
        }
        return;
      }

      try {
        setIsLoading(true);
        setCasesPage(1);
        setNotesPage(1);

        const songsRes = await apiClient(`/albums/${album.id}/songs?page=1&limit=500`);
        const rawSongs = Array.isArray(songsRes?.songs)
          ? songsRes.songs
          : Array.isArray(songsRes?.data)
            ? songsRes.data
            : [];

        const activeSongsOnly = rawSongs.filter((song) => {
          const status = song?.status ?? song?.song_status ?? song?.songStatus ?? song?.songalbum_status ?? song?.album_status;
          const isDeleted = song?.is_delete ?? song?.is_deleted ?? song?.isDelete ?? song?.isDeleted ?? song?.songalbum_is_delete;
          const normalizedStatus = status === undefined || status === null || status === ''
            ? true
            : status === 1 || status === '1' || status === 'Active' || status === 'active' || status === true;
          const normalizedDeleted = isDeleted === undefined || isDeleted === null || isDeleted === ''
            ? true
            : isDeleted === 0 || isDeleted === '0' || isDeleted === false;
          return normalizedStatus && normalizedDeleted;
        });

        const songIds = activeSongsOnly.map((song) => song?.id || song?.song_id || song?.songId).filter(Boolean);

        if (!songIds.length) {
          if (isMounted) {
            setCasesList([]);
            setNotesList([]);
          }
          return;
        }

        const queryParams = new URLSearchParams({
          songIds: songIds.join(','),
          limit: '100',
          status: 'all'
        });

        const res = await apiClient(`/notes-and-cases?${queryParams.toString()}`);
        const items = Array.isArray(res?.items) ? res.items : [];
        const cases = items.filter((item) => item?.type === 'case');
        const notes = items.filter((item) => item?.type === 'note');

        if (isMounted) {
          setCasesList(cases);
          setNotesList(notes);
        }
      } catch (err) {
        console.error('Error fetching album notes and cases:', err);
        if (isMounted) {
          toast.error('Failed to load notes and cases for this album.');
          setCasesList([]);
          setNotesList([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAlbumNotesAndCases();
    return () => {
      isMounted = false;
    };
  }, [album?.id]);

  const pagedCases = useMemo(() => paginateItems(casesList, casesPage, 20), [casesList, casesPage]);
  const pagedNotes = useMemo(() => paginateItems(notesList, notesPage, 20), [notesList, notesPage]);

  return (
    <div className="cases-notes-tab-container" style={{ position: 'relative' }}>
      <LoadingOverlay isLoading={isLoading} message="Loading Notes & Cases..." />

      <div className="cases-section">
        <div className="section-header-row">
          <div className="section-title-left">
            <div className="section-icon-circle circle-indigo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5">
                <line x1="12" y1="22" x2="12" y2="2" />
                <path d="M5 22h14" />
                <path d="m19 16-5-3 5-3v6Z" />
                <path d="m5 16 5-3-5-3v6Z" />
              </svg>
            </div>
            <h3 className="section-title-label">{t('albumDetails.casesTitle')}</h3>
            <span className="section-count-pill">{casesList.length} {t('albumDetails.casesCount')}</span>
          </div>
        </div>

        {casesList.length === 0 ? (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
            <EmptyState title="No Cases Logged" subtitle="No active cases are currently registered for the songs in this album." hasSearch={false} />
          </div>
        ) : (
          <>
            <div className="cases-grid">
              {pagedCases.map((item) => {
                const isOpen = item.status === 1 || item.status === '1';
                const statusKey = isOpen ? 'casesStatusOpen' : 'casesStatusClosed';
                const statusClass = isOpen ? 'status-open' : 'status-closed';

                return (
                  <div key={item.id || `${item.type}-${item.name || item.title}`} className="case-card">
                    <div className="case-card-top">
                      <div className="case-icon-wrapper">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="case-info-details">
                        <h4 className="case-title">{item.name || item.title}</h4>
                        <span className="case-tags">Tags: {item.tags || item.description || 'Standard Case'}</span>
                      </div>
                    </div>

                    <div className="case-situation-box">
                      <span className="situation-title">Current Situation</span>
                      <div className={`situation-status-pill ${statusClass}`}>
                        <span className="status-dot"></span>
                        <span className="status-text">{t(`albumDetails.${statusKey}`) || item.status || 'Open'}</span>
                      </div>
                    </div>

                    <div className="case-card-footer">
                      <button type="button" className="view-more-btn" onClick={() => onSelectCase && onSelectCase(item)}>
                        {t('albumDetails.casesViewMore')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination
              currentPage={casesPage}
              totalPages={Math.max(1, Math.ceil(casesList.length / 20))}
              totalCount={casesList.length}
              onPageChange={setCasesPage}
            />
          </>
        )}
      </div>

      <hr className="sections-divider" />

      <div className="notes-section">
        <div className="section-header-row">
          <div className="section-title-left">
            <div className="section-icon-circle circle-blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3 className="section-title-label">{t('albumDetails.notesTitle')}</h3>
            <span className="section-count-pill">{notesList.length} {t('albumDetails.notesCount')}</span>
          </div>
        </div>

        {notesList.length === 0 ? (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
            <EmptyState title="No Notes Logged" subtitle="No notes are currently registered for the songs in this album." hasSearch={false} />
          </div>
        ) : (
          <>
            <div className="notes-grid">
              {pagedNotes.map((note) => {
                const isOpen = note.status === 1 || note.status === '1';
                return (
                  <div key={note.id || `${note.type}-${note.name || note.title}`} className="note-card">
                    <div className="note-card-body">
                      <div className="note-icon-wrapper">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div className="note-info-details">
                        <h4 className="note-title">{note.name || note.title}</h4>
                        <span className="note-tags">Tags: {note.tags || note.description || 'Standard Note'}</span>
                      </div>
                    </div>

                    <div className="note-card-footer">
                      <button type="button" className="view-more-btn" onClick={() => onSelectCase && onSelectCase(note)}>
                        {t('albumDetails.notesViewMore')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination
              currentPage={notesPage}
              totalPages={Math.max(1, Math.ceil(notesList.length / 20))}
              totalCount={notesList.length}
              onPageChange={setNotesPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default CasesNotesTab;
